import { Button, Container, Icon, Popover, StatusIndicator } from "@amzn/awsui-components-react";
import * as React from "react";
import { AppURLFactory } from "../../factory/app-url-factory";
import "../../styles/component/container/container-view-v2.scss";

/**
 * Shared card shell for containers in the PathFinder Agentic UI lab (live page and draft view):
 * a dark header strip we own (no styling of Cloudscape internals) and a padded body.
 * Pass `expanded` + `onToggle` for a collapsible card; omit them for a static card.
 */
export const ContainerCardV2 = (props: {
    title: React.ReactNode;
    bodyId: string;
    expanded?: boolean;
    onToggle?: () => void;
    /** Rendered right after the title, e.g. a favorite or info button */
    headerExtras?: React.ReactNode;
    /** Rendered at the right end of the header */
    actions?: React.ReactNode;
    /** Extra classes for the body wrapper */
    bodyClassName?: string;
    children: React.ReactNode;
}) => {
    const isCollapsible = props.expanded !== undefined;
    const isExpanded = !isCollapsible || props.expanded;

    return (
        <Container
            disableHeaderPaddings
            disableContentPaddings
            header={
                <div className={`pf-card-header${isExpanded ? "" : " pf-card-header--collapsed"}`}>
                    {isCollapsible ? (
                        <button
                            type="button"
                            className="pf-card-toggle"
                            aria-expanded={isExpanded}
                            aria-controls={props.bodyId}
                            onClick={props.onToggle}
                        >
                            <Icon
                                name={isExpanded ? "caret-down-filled" : "caret-right-filled"}
                                variant="inverted"
                            />
                            <span className="pf-card-title">{props.title}</span>
                        </button>
                    ) : (
                        <span className="pf-card-title">{props.title}</span>
                    )}
                    {props.headerExtras}
                    {props.actions && (
                        <div className="pf-card-actions awsui-polaris-dark-mode">
                            {props.actions}
                        </div>
                    )}
                </div>
            }
        >
            <div
                id={props.bodyId}
                className={`pf-card-body${props.bodyClassName ? ` ${props.bodyClassName}` : ""}${isExpanded ? "" : " pf-card-body--collapsed"}`}
            >
                {props.children}
            </div>
        </Container>
    );
};

/** Copies the container's live-page link; anchor icon so it does not read as "copy content". */
export const ContainerLinkCopyButtonV2 = (props: { containerId: string }) => {
    const [copyLinkStatus, setCopyLinkStatus] = React.useState<"success" | "error">("success");
    const containerUrl = window.location.origin + AppURLFactory.getContainer(props.containerId);
    return (
        <Popover
            size="small"
            position="top"
            triggerType="custom"
            dismissButton={false}
            renderWithPortal
            content={copyLinkStatus === "error"
                ? <StatusIndicator type="error">Container link failed to copy</StatusIndicator>
                : <StatusIndicator type="success">Container link copied</StatusIndicator>}
        >
            <Button
                variant="icon"
                iconName="anchor-link"
                ariaLabel="Copy container link"
                onClick={() => {
                    if (!navigator.clipboard) {
                        setCopyLinkStatus("error");
                        return;
                    }
                    navigator.clipboard.writeText(containerUrl)
                        .then(() => setCopyLinkStatus("success"))
                        .catch(() => setCopyLinkStatus("error"));
                }}
            />
        </Popover>
    );
};

export const BackToTopButtonV2 = () => (
    <Button
        variant="icon"
        iconName="arrow-up"
        ariaLabel="Back to top"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    />
);

/** Info icon in a dark card header that opens a details popover. */
export const CardInfoPopoverV2 = (props: {
    header: string;
    content: React.ReactNode;
}) => (
    <span className="awsui-polaris-dark-mode">
        <Popover
            position="bottom"
            size="large"
            triggerType="custom"
            renderWithPortal
            header={props.header}
            content={props.content}
        >
            <Button variant="inline-icon" iconName="status-info" ariaLabel={props.header} />
        </Popover>
    </span>
);
